import {
    SliceFactory,
    FetchCollectionData,
    ApiCollectionResponse,
    FetchItemDataType,
} from '@europrocurement/l2d-redux-utils';
import { Order } from '@europrocurement/l2d-domain/types/apiGenericTypes';
import {
    ConfigurationParameters as DossiersConfigurationParameters,
    Configuration,
    FormaliteApiApiFormalitesIdGetRequest,
    FormaliteArticleOpenSearchApi,
    FormaliteArticleOpenSearchApiApiOpensearchFormalitesGetCollectionRequest,
    FormaliteArticleOpenSearchJsonldOpensearchFormaliteArticleRead,
} from '../../../openApi/ApiFormalite';
import { FORMALITES_REDUCER_NAME, FORMALITES_SLICE_NAME } from '../constants';

export type FormaliteApiObject = FormaliteArticleOpenSearchJsonldOpensearchFormaliteArticleRead &
    Record<string, unknown>;

export type Formalite = FormaliteApiObject & {
    validators: FormalityValidator[];
};

export type FormalityValidator = {
    validator: number;
    default: boolean;
    forms: boolean;
    documents: boolean;
};

export const mapperFormalites = function (formality: Formalite) {
    return {
        ...formality,
        legalStatuses: formality.legalStatuses,
        compatibilities: formality.compatibilities || [],
        documents: formality.documents,
        validators: formality.validators || [],
    } as unknown as Formalite;
};

export function createFormaliteSlice(configuration: DossiersConfigurationParameters) {
    const conf = new Configuration(configuration);
    const api = new FormaliteArticleOpenSearchApi(conf);

    const fetchFormaliteCollection: FetchCollectionData<Formalite> = ({
        search,
        pagination,
        filters,
        orders,
    }) => {
        const options = undefined;
        let orderLibelle: Order;
        let orderCategorieLibelle: Order;
        let orderRang: Order;
        let orderCharge: string | undefined;
        let libelle: string | undefined;
        let categorieId: number | undefined;
        let multiFieldSearch: string | undefined;

        if (filters.libelle) {
            libelle = filters.libelle as string;
        }

        if (filters.categorie) {
            categorieId = filters.categorie as number;
        }

        orders.forEach((item) => {
            if (item.field === 'libelle') {
                orderLibelle = item.value;
            }
            if (item.field === 'categorie') {
                orderCategorieLibelle = item.value;
            }
            if (item.field === 'rang') {
                orderRang = item.value;
            }
            if (item.field === 'charge') {
                orderCharge = item.value;
            }
        });

        if (search && search !== '') {
            multiFieldSearch = search;
        }

        const payload: Record<string, number | number[] | string | undefined | boolean> = {
            page: pagination.page + 1,
            itemsPerPage: pagination.itemsPerPage,
            // filters
            libelle,
            categorieId: [categorieId] as number[],
            q: multiFieldSearch,
            // orders
            orderLibelle,
            orderCategorieLibelle,
            orderRang,
            orderCharge,
        };

        if (typeof filters.deleted !== 'undefined') {
            payload.deleted = !!filters.deleted;
        }

        const requestParameters: FormaliteArticleOpenSearchApiApiOpensearchFormalitesGetCollectionRequest =
            { ...payload };

        return api
            .apiOpensearchFormalitesGetCollection(requestParameters, options)
            .then((res) => res.data) as Promise<ApiCollectionResponse<Formalite>>;
    };

    const fetchFormaliteItem: FetchItemDataType<Formalite> = ({ idItem }) => {
        const requestParameters: FormaliteApiApiFormalitesIdGetRequest = {
            id: String(idItem),
        };

        return api
            .apiOpensearchFormalitesIdGet(requestParameters)
            .then((res) => res.data) as Promise<Formalite>;
    };

    const { slice: formaliteSlice, dataSourcesThunks: formaliteDataSourcesThunks } =
        SliceFactory.createSlice<Formalite, Formalite>(
            FORMALITES_SLICE_NAME,
            FORMALITES_REDUCER_NAME,
            [],
            fetchFormaliteCollection,
            fetchFormaliteItem,
            mapperFormalites,
            {
                deleted: false,
            },
            {},
        );

    return { formaliteSlice, formaliteDataSourcesThunks };
}
